import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import RoutePreviewMap from '../components/RoutePreviewMap';
import {
  DASHBOARD_POLL_MS,
  conflictMessage,
  formatKm,
  formatMinutes,
  formatPct,
  vehicleLabel,
} from '../lib/logisticsUi';
import './DashboardPages.css';

const TABS = [
  { id: 'proposed', label: 'Proposed' },
  { id: 'active', label: 'Active' },
  { id: 'completed', label: 'Completed' },
];

function routeTab(route) {
  if (route.status === 'proposed') return 'proposed';
  if (route.status === 'cancelled') return null;
  if (route.status === 'completed' || route.trip_status === 'delivered') return 'completed';
  return 'active';
}

function RoutesPage() {
  const [routes, setRoutes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [tab, setTab] = useState('proposed');
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [workingId, setWorkingId] = useState(null);
  const [reassign, setReassign] = useState(null);
  const [skippingId, setSkippingId] = useState(null);

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const [{ routes: rows }, driverData] = await Promise.all([
        api.getRoutes(),
        api.getDrivers().catch(() => ({ drivers: [] })),
      ]);
      setRoutes(rows || []);
      setDrivers(driverData.drivers || []);
    } catch (err) {
      setError(err.message);
      if (!silent) setRoutes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => load(true), DASHBOARD_POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const filtered = useMemo(
    () => routes.filter((r) => routeTab(r) === tab),
    [routes, tab]
  );

  const selected = useMemo(
    () => routes.find((r) => Number(r.route_id) === Number(selectedId)) || filtered[0] || null,
    [routes, selectedId, filtered]
  );

  const handleAccept = async (route, driverId) => {
    try {
      setWorkingId(route.route_id);
      setError(null);
      await api.acceptRoute(route.route_id, driverId);
      setSuccess(`Route #${route.route_id} accepted.`);
      setReassign(null);
      await load();
    } catch (err) {
      setError(err.status === 409 ? conflictMessage(err) : err.message);
    } finally {
      setWorkingId(null);
    }
  };

  const handleCancel = async (route) => {
    if (!window.confirm(`Cancel proposed route #${route.route_id}?`)) return;
    try {
      setWorkingId(route.route_id);
      setError(null);
      await api.cancelRoute(route.route_id);
      setSuccess(`Route #${route.route_id} cancelled.`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setWorkingId(null);
    }
  };

  const handleSkipStop = async (tripId, stopId) => {
    if (!window.confirm('Skip this stop? The driver app will jump to the next pending stop.')) return;
    try {
      setSkippingId(stopId);
      setError(null);
      await api.skipTripStop(tripId, stopId);
      setSuccess('Stop skipped.');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSkippingId(null);
    }
  };

  return (
    <div className="dashboard-page">
      <h1>Routes</h1>
      <p className="dashboard-subtitle">
        Proposed plans wait for acceptance. Active routes show live stop progress.
      </p>

      <div className="orders-toolbar route-tabs">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`route-tab ${tab === item.id ? 'is-active' : ''}`}
            onClick={() => {
              setTab(item.id);
              setSelectedId(null);
            }}
          >
            {item.label}
          </button>
        ))}
        <button type="button" className="orders-refresh-btn" onClick={() => load()}>
          Refresh
        </button>
      </div>

      {success && <div className="orders-success">{success}</div>}
      {error && <div className="orders-error">{error}</div>}

      {loading ? (
        <div className="orders-placeholder"><p>Loading routes…</p></div>
      ) : (
        <div className="routes-layout">
          <div className="routes-list-col">
            {filtered.length === 0 ? (
              <div className="orders-placeholder">
                <p>No {tab} routes.</p>
              </div>
            ) : (
              filtered.map((route) => {
                const active = selected && Number(selected.route_id) === Number(route.route_id);
                return (
                  <button
                    type="button"
                    key={route.route_id}
                    className={`order-card route-list-card ${active ? 'is-selected' : ''}`}
                    onClick={() => setSelectedId(route.route_id)}
                  >
                    <div className="order-card-header">
                      <span className="order-id">Route #{route.route_id}</span>
                      <span className="status-badge">{route.trip_status || route.status}</span>
                    </div>
                    <div className="order-card-body">
                      <p><strong>Driver:</strong> {route.driver_name || 'Unassigned'}</p>
                      <p><strong>Vehicle:</strong> {vehicleLabel(route.vehicle_type)}</p>
                      <p><strong>Planned:</strong> {formatKm(route.planned_distance_km)} · saved {formatKm(route.savings_km)}</p>
                      {tab === 'completed' && (
                        <p>
                          <strong>Fill:</strong> {formatPct(route.utilization)} · {formatMinutes(route.planned_duration_min)} planned
                        </p>
                      )}
                    </div>
                    {tab === 'proposed' && (
                      <div className="entity-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="entity-btn entity-edit"
                          disabled={workingId === route.route_id || !route.driver_id}
                          onClick={() => handleAccept(route, route.driver_id)}
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          className="entity-btn"
                          disabled={workingId === route.route_id}
                          onClick={() => setReassign(route)}
                        >
                          Reassign
                        </button>
                        <button
                          type="button"
                          className="entity-btn entity-delete"
                          disabled={workingId === route.route_id}
                          onClick={() => handleCancel(route)}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
          <div className="routes-detail-col">
            {selected ? (
              <>
                <h2 className="entity-list-heading">
                  Route #{selected.route_id}
                  {selected.trip_id ? ` · Trip #${selected.trip_id}` : ''}
                </h2>
                {tab === 'completed' && selected.pickup_time && selected.delivery_time && (
                  <p className="orders-hint">
                    Actual {formatMinutes((new Date(selected.delivery_time) - new Date(selected.pickup_time)) / 60000)}
                    {' vs planned '}{formatMinutes(selected.planned_duration_min)}
                  </p>
                )}
                <RoutePreviewMap
                  route={selected}
                  tripId={selected.trip_id}
                  onSkipStop={tab === 'active' ? handleSkipStop : undefined}
                  skippingId={skippingId}
                />
              </>
            ) : (
              <div className="orders-placeholder"><p>Select a route to view the stop timeline.</p></div>
            )}
          </div>
        </div>
      )}

      {reassign && (
        <div className="modal-overlay" onClick={() => setReassign(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Reassign route #{reassign.route_id}</h3>
            <ul className="driver-list">
              {drivers.filter((d) => d.active !== false).map((d) => (
                <li key={d.driver_id}>
                  <button
                    type="button"
                    className="driver-select-btn"
                    disabled={workingId === reassign.route_id}
                    onClick={() => handleAccept(reassign, d.driver_id)}
                  >
                    {d.name} · {vehicleLabel(d.vehicle_type)}
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" className="modal-close-btn" onClick={() => setReassign(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoutesPage;
